import { useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { styled, useTheme } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, Avatar, useMediaQuery } from '@mui/material';
// components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { adminNavConfig, teacherNavConfig, studentNavConfig, filterNavByPermission } from './NavConfig';
import { getRoleBaseRoute, hasRolePermission, isKnownRole } from 'src/config/permissions';
import { useAuth } from '../../context/auth/AuthContext';
import { GoldMindsLogo } from 'src/images';

const DRAWER_WIDTH = 240; // Reduced width

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  paddingBottom: theme.spacing(2),
}));

const UserInfoStyle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '16px',
});

const UserNameStyle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#1A1A1A',
  marginBottom: '2px',
});

const UserRoleStyle = styled(Typography)({
  fontSize: '12px',
  color: '#718096',
  textTransform: 'uppercase',
});

interface DashboardSidebarProps {
  isOpenSidebar: boolean;
  onCloseSidebar: () => void;
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();
  const { user, rolesVersion } = useAuth();

  // Menu picked by base route, then filtered by permission.
  const navConfig = useMemo(() => {
    const role = user?.role;
    if (!isKnownRole(role)) return [];
    const sections: Record<string, typeof adminNavConfig> = {
      '/admin': adminNavConfig,
      '/teacher': teacherNavConfig,
      '/student': studentNavConfig,
    };
    const items = sections[getRoleBaseRoute(role)] || [];
    return filterNavByPermission(items, (permission) => hasRolePermission(role, permission));
    // rolesVersion: role permissions can change at runtime from the Roles page.
  }, [user?.role, rolesVersion]);
  useEffect(() => {
    if (isOpenSidebar && !isDesktop) {
      onCloseSidebar();
    }
  }, [pathname, isDesktop]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100vh',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <AccountStyle sx={{ pt: 1.25, pb: 0.5 }}>
        <Box
          component="img"
          src={GoldMindsLogo}
          alt="Gold Minds Logo"
          sx={{
            width: 48,
            height: 48,
            objectFit: 'contain',
          }}
        />
      </AccountStyle>

      <Box sx={{ px: 1, pb: 1 }}>
        <NavSection navConfig={navConfig} />
      </Box>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle>
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: '#FFFFFF',
              borderRight: '1px solid #E2E8F0',
              boxShadow: 'none',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: '#FFFFFF',
              borderRight: '1px solid #E2E8F0',
              boxShadow: 'none',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
